import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';
import Image from 'next/image';

import type { CartPayment } from '@eliadmin/core/src/api/cart/cart-types';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { RadioGroupItem } from '@eliadmin/core/src/components/ui/radio-group';
import { useCartQueryOpts } from '@eliadmin/core/src/hooks/cart/use-cart';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LuCheckCircle, LuCircle } from 'react-icons/lu';
import { FaCalculator } from 'react-icons/fa';
import { QuatroPoppup } from '@eliadmin/core/src/components/cart/detail/quatro/quatro-poppup';
import { Dialog, DialogProvider, DialogTrigger } from '@eliadmin/core/src/components/ui/dialog';
import { HtmlRenderer } from '@eliadmin/core/src/components/ui/html-renderer';
import StripeCardForm from '@eliadmin/core/src/components/cart/detail/stripe/stripe-card-form';

type CartPaymentProps = {
  payment: CartPayment;
};

export function CartPayment({ payment }: CartPaymentProps) {
  const currencySettings = useGlobalStore(state => state.generalLayout.currency_settings);
  const currency = useGlobalStore(state => state.generalLayout.currency);
  const { t } = useTranslations();

  const { data: cart } = useQuery(useCartQueryOpts());

  const activePaymentId = cart?.paymentType?.id;
  const isActivePayment = String(activePaymentId) === String(payment.id);

  const queryClient = useQueryClient();
  const isPaymentTypesMutating = queryClient.isMutating({ mutationKey: [CacheKey.CART_PAYMENTS] });
  // const isShipmentTypesMutating = queryClient.isMutating({ mutationKey: [CacheKey.SHIPMENTS] });

  if (
    isActivePayment &&
    payment.gateway === 'stripe' &&
    payment.usecase === 'embedded' &&
    !payment.settings?.gateway?.client_secret
  ) {
    queryClient.invalidateQueries({ queryKey: [CacheKey.CART_PAYMENTS] });
  }

  return (
    <Label
      htmlFor={`payment-${payment.id}`}
      className={cn(
        'flex w-full cursor-pointer flex-row items-center justify-between gap-4 rounded-[4px] p-2 sm:p-3',
        isActivePayment && 'bg-primary-foreground',
        'ea-cart-payment',
      )}
    >
      <div className={cn('flex w-full flex-col gap-4', 'ea-cart-payment__wrapper')}>
        <div className={cn('flex w-full flex-col gap-2', 'ea-cart-payment__container-wrapper')}>
          <div
            className={cn(
              'flex flex-row items-center justify-between',
              'ea-cart-payment__container',
            )}
          >
            <div
              className={cn(
                'flex flex-row items-center gap-2',
                'ea-cart-payment__container-in-container',
              )}
            >
              <RadioGroupItem
                id={`payment-${payment.id}`}
                checked={isActivePayment}
                value={String(payment.id)}
                disabled={!!isPaymentTypesMutating}
                className={cn('bg-white', 'ea-cart-payment__radio-group-item')}
              />
              {/* TODO: placeholder */}
              <div
                className={cn(
                  'flex max-h-10 min-h-10 min-w-10 max-w-10 items-center justify-center gap-2 rounded-[4px] bg-white p-0',
                  'ea-cart-payment__image',
                )}
              >
                {payment.image && (
                  <Image
                    alt={resolveImageAlt(payment?.image, payment?.name)}
                    src={payment.image.url}
                    width={0}
                    height={0}
                    className={cn('ea-cart-payment__image', 'h-7 w-7 object-contain')}
                    sizes="40px"
                  />
                )}
              </div>
              <span className={cn('h6 font-bold', 'ea-cart-payment__name')}>{payment.name}</span>
            </div>
            <span
              className={cn(
                // `uppercase` only on the "Zdarma" label: on prices it rendered the currency as "KČ".
                'justify-self-end whitespace-nowrap font-bold',
                'h6',
                payment.price_with_vat && 'text-black',
                !payment.price_with_vat && 'uppercase tracking-tight text-[#008000]',
              )}
            >
              {payment.price_with_vat
                ? formatPrice(payment.price_with_vat, currency, currencySettings)
                : t('text426')}
            </span>
          </div>
          {payment.gateway === 'stripe' &&
            payment.usecase === 'embedded' &&
            isActivePayment &&
            !!payment.settings?.gateway?.client_secret && (
              <StripeCardForm clientSecret={payment.settings?.gateway?.client_secret} />
            )}
          {payment.gateway === 'homecredit' && isActivePayment && (
            <HtmlRenderer html={payment.description} className="font-medium [&_p]:text-xs" />
          )}
          {payment.gateway === 'quatro' && cart?.items_total && (
            <div className={cn('pl-8', 'ea-cart-payment__quatro-calculator-wrapper')}>
              <DialogProvider>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button
                      variant={'default'}
                      className={cn(
                        'w-max gap-2 rounded-[8px]',
                        'ea-cart-payment__quatro-calculator-trigger',
                      )}
                      size={'sm'}
                    >
                      <FaCalculator />
                      {t('text816')}
                    </Button>
                  </DialogTrigger>
                  <QuatroPoppup price={cart.items_total} />
                </Dialog>
              </DialogProvider>
            </div>
          )}
        </div>
        {isActivePayment && !!payment.discounts?.length && (
          <div className={cn('flex flex-col gap-3 px-4', 'ea-cart-payment__discounts')}>
            {payment.discounts?.map(discount => (
              <div
                className={cn('flex flex-row items-center gap-4', 'ea-cart-payment__discount')}
                key={discount.id}
              >
                {discount.is_active ? (
                  <LuCheckCircle
                    size={24}
                    className={cn(
                      'fill-primary-foreground text-success-foreground',
                      'ea-cart-payment__discount-active-icon',
                    )}
                  />
                ) : (
                  <LuCircle
                    size={24}
                    className={cn(
                      'fill-primary-foreground text-border',
                      'ea-cart-payment__discount-not-active-icon',
                    )}
                  />
                )}
                <span className={cn('text-xs font-bold', 'ea-cart-payment__discount-text')}>
                  {t('text24')} {discount.discount}
                  {discount.discount_type === 'fixed' ? currency : '%'} {t('text479')}{' '}
                  {discount.min}
                  {currency}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Label>
  );
}
